/**
 * Tiny fetch wrapper for the real BE.
 *
 * Why this exists:
 *   - Every FE call must hit the same base URL (configurable per env via
 *     `VITE_API_BASE_URL`) so the same FE build runs against `wrangler dev`
 *     locally and `https://api.pojisteno.cz` in prod.
 *   - Every call must include credentials so the `srab_session` HttpOnly
 *     cookie set by the BE is sent on subsequent requests (login + auto-account
 *     upload + every authenticated read).
 *   - 4xx/5xx must become exceptions so TanStack Query's `onError` path runs;
 *     the upstream hook then decides whether to swallow (e.g. 401 → `null`
 *     family) or propagate to the UI.
 *
 * Schema validation:
 *   - When a parser is provided (any object with a `.parse(unknown): T`
 *     method — i.e. a Zod schema), the response body is run through it so a
 *     drifted BE shape fails loudly in dev. Hooks pass the `@srab/shared`
 *     schemas; raw debugging paths (e.g. `/ocr-test`) can skip parsing.
 *
 * Keep this file dependency-light. We deliberately don't import `zod`
 * directly — `@srab/shared` is the only allowed surface for that dep, and
 * its schemas already conform to the structural `Parser<T>` below.
 */

/**
 * Minimal structural type that every Zod schema satisfies. Lets us accept
 * any `ZodSchema<T>` (and anything else with a `parse` method of the same
 * shape) without taking a direct `zod` import.
 */
export type Parser<T> = { parse: (input: unknown) => T }

const BASE: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  'http://localhost:8787'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function buildUrl(path: string): string {
  // Allow callers to pass either '/api/foo' or 'api/foo'; normalise.
  const sep = path.startsWith('/') ? '' : '/'
  return `${BASE}${sep}${path}`
}

async function parseJson(res: Response, path: string): Promise<unknown> {
  // Empty body is a legitimate 204/no-content response. Treat as undefined.
  const text = await res.text()
  if (!text) return undefined
  try {
    return JSON.parse(text)
  } catch {
    throw new ApiError(
      res.status,
      path,
      `invalid_json: ${text.slice(0, 80)}`,
    )
  }
}

async function handleResponse<T>(
  res: Response,
  path: string,
  schema?: Parser<T>,
): Promise<T> {
  if (!res.ok) {
    // Try to surface the server-provided error code for clearer logs.
    let detail = ''
    try {
      const body = (await parseJson(res, path)) as { error?: string } | undefined
      if (body && typeof body.error === 'string') detail = `: ${body.error}`
    } catch {
      /* swallow — we still want the status-only error */
    }
    throw new ApiError(res.status, path, `request_failed (${res.status})${detail}`)
  }
  const body = await parseJson(res, path)
  if (!schema) return body as T
  return schema.parse(body)
}

export async function apiGet<T>(
  path: string,
  schema?: Parser<T>,
): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'GET',
    credentials: 'include',
  })
  return handleResponse<T>(res, path, schema)
}

export async function apiPostJson<T>(
  path: string,
  body: unknown,
  schema?: Parser<T>,
): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body == null ? undefined : JSON.stringify(body),
  })
  return handleResponse<T>(res, path, schema)
}

export async function apiPostMultipart<T>(
  path: string,
  form: FormData,
  schema?: Parser<T>,
): Promise<{ data: T; headers: Headers }> {
  // NOTE: do NOT set Content-Type manually — the browser must inject the
  // multipart boundary parameter. Setting it manually breaks the parse on
  // wrangler dev and Hono returns 400.
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    credentials: 'include',
    body: form,
  })
  const data = await handleResponse<T>(res, path, schema)
  return { data, headers: res.headers }
}

/** Exposed for tests / dev probes that want to read the configured base. */
export const API_BASE_URL = BASE
